import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Cpu,
  Database,
  Zap,
  CheckCircle,
  ArrowRight,
  Users,
  TrendingUp,
  Target,
  BarChart3,
  Shield,
  Settings,
  Play,
  Download,
  Star,
  Clock,
  DollarSign,
  Building2,
  Lock,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  ExternalLink,
  Lightbulb,
  PieChart,
  Activity,
  Wifi,
  Globe,
  Monitor,
  Smartphone,
  Globe2,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  Bot,
  Network,
  Layers,
  Code,
  GitBranch,
  GitCommit,
  GitPullRequest,
  GitMerge,
  GitCompare,
  GitFork,
  ShoppingBag,
  Heart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AI from "../components/assets/ai.jpeg";
import AI2 from "../components/assets/ai2.jpg";
import AI3 from "../components/assets/ai2.jpeg";
import CommonContactUs from "./CommonContactUs";

const AIPoweredPage = (props) => {
  const [activeTab, setActiveTab] = useState("overview");

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderImages = [AI, AI2, AI3];

  const goToPrev = () =>
    setCurrentSlide((prev) =>
      prev === 0 ? sliderImages.length - 1 : prev - 1
    );
  const goToNext = () =>
    setCurrentSlide((prev) =>
      prev === sliderImages.length - 1 ? 0 : prev + 1
    );

  const services = [
    {
      icon: <Brain className="w-8 h-8 text-blue-600" />,
      title: "Machine Learning",
      description:
        "Build intelligent systems that learn from data and make predictions with high accuracy.",
      features: [
        "Predictive analytics",
        "Pattern recognition",
        "Automated decision making",
        "Model training & deployment",
      ],
    },
    {
      icon: <Bot className="w-8 h-8 text-green-600" />,
      title: "AI Chatbots",
      description:
        "Intelligent conversational agents that provide 24/7 customer support and engagement.",
      features: [
        "Natural language processing",
        "Multi-language support",
        "Integration with CRM",
        "Sentiment analysis",
      ],
    },
    {
      icon: <Eye className="w-8 h-8 text-purple-600" />,
      title: "Computer Vision",
      description:
        "AI-powered image and video analysis for automation and quality control.",
      features: [
        "Object detection",
        "Image classification",
        "Facial recognition",
        "Video analytics",
      ],
    },
    {
      icon: <Search className="w-8 h-8 text-orange-600" />,
      title: "Intelligent Search",
      description:
        "Advanced search capabilities with semantic understanding and personalized results.",
      features: [
        "Semantic search",
        "Recommendation engines",
        "Content optimization",
        "Search analytics",
      ],
    },
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Increased Efficiency",
      description:
        "Automate repetitive tasks and processes to boost productivity.",
    },
    {
      icon: <Target className="w-8 h-8 text-green-600" />,
      title: "Better Insights",
      description:
        "Extract meaningful patterns and insights from large datasets.",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-purple-600" />,
      title: "Cost Reduction",
      description:
        "Reduce operational costs through intelligent automation and optimization.",
    },
    {
      icon: <Users className="w-8 h-8 text-orange-600" />,
      title: "Enhanced Experience",
      description: "Provide personalized and intelligent user experiences.",
    },
  ];

  const aiTechnologies = [
    {
      name: "TensorFlow & PyTorch",
      description:
        "Leading deep learning frameworks for building neural networks",
      icon: <Brain className="w-6 h-6" />,
    },
    {
      name: "Natural Language Processing",
      description:
        "Advanced text analysis and language understanding capabilities",
      icon: <MessageSquare className="w-6 h-6" />,
    },
    {
      name: "Computer Vision",
      description:
        "Image and video analysis with object detection and recognition",
      icon: <Eye className="w-6 h-6" />,
    },
    {
      name: "Recommendation Systems",
      description: "Personalized content and product recommendations",
      icon: <Target className="w-6 h-6" />,
    },
    {
      name: "Predictive Analytics",
      description: "Forecast trends and outcomes using historical data",
      icon: <BarChart3 className="w-6 h-6" />,
    },
    {
      name: "Automated Machine Learning",
      description: "Streamlined ML model development and deployment",
      icon: <Settings className="w-6 h-6" />,
    },
  ];

  const useCases = [
    {
      industry: "E-commerce",
      applications: [
        "Product recommendations",
        "Price optimization",
        "Inventory forecasting",
        "Customer segmentation",
      ],
      icon: <ShoppingBag className="w-8 h-8 text-blue-600" />,
    },
    {
      industry: "Healthcare",
      applications: [
        "Medical diagnosis",
        "Drug discovery",
        "Patient monitoring",
        "Treatment optimization",
      ],
      icon: <Heart className="w-8 h-8 text-red-600" />,
    },
    {
      industry: "Finance",
      applications: [
        "Fraud detection",
        "Risk assessment",
        "Algorithmic trading",
        "Credit scoring",
      ],
      icon: <DollarSign className="w-8 h-8 text-green-600" />,
    },
    {
      industry: "Manufacturing",
      applications: [
        "Quality control",
        "Predictive maintenance",
        "Supply chain optimization",
        "Process automation",
      ],
      icon: <Settings className="w-8 h-8 text-purple-600" />,
    },
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Johnson",
      role: "Chief Data Scientist",
      company: "AI Innovations Inc",
      content:
        "TechMind's AI solutions helped us achieve 95% accuracy in our predictive models, transforming our business operations.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "VP of Technology",
      company: "Smart Retail",
      content:
        "The AI-powered recommendation engine increased our sales by 40% and improved customer satisfaction significantly.",
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "CTO",
      company: "Digital Health",
      content:
        "Their computer vision solution automated our quality control process, reducing defects by 80%.",
      rating: 5,
    },
  ];

  // Auto-advance slider every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === sliderImages.length - 1 ? 0 : prev + 1
      );
    }, 8000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);
  const scrollIntoView = () => {
    const element = document.getElementById("learn-more-ai");
    if (element) {
      const yOffset = -80; // adjust based on your header height
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section with Slider */}
      {props?.from === "Home" ? null : (
        <section className="relative bg-[#1A193B] h-[716px] text-white overflow-hidden pt-24">
          <div className="absolute inset-0 bg-black/20 z-10"></div>
          {/* Slider Images */}
          <div className="absolute inset-0 w-full h-full flex transition-all duration-700 z-0">
            {sliderImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt="AI Slide"
                className={`object-cover w-full h-full absolute top-0 left-0 transition-opacity duration-700 ${
                  currentSlide === idx ? "opacity-60 z-20" : "opacity-0 z-0"
                }`}
                style={{ transition: "opacity 0.7s" }}
              />
            ))}
          </div>
          {/* Slider Controls */}
          <div className="absolute left-0 right-0 flex justify-between items-center h-full px-4 z-30 pointer-events-none">
            <button
              onClick={goToPrev}
              className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
              aria-label="Previous Slide"
              style={{ top: "50%", transform: "translateY(-50%)" }}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button
              onClick={goToNext}
              className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
              aria-label="Next Slide"
              style={{ top: "50%", transform: "translateY(-50%)" }}
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          </div>
          {/* Dots */}
          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30"
            style={{ zIndex: "42" }}
          >
            {sliderImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-3 h-3 rounded-full ${
                  currentSlide === idx ? "bg-white" : "bg-white/40"
                } border border-white transition-all`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
          {/* Hero Content */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-56 z-40">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                AI Powered
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-purple-100">
                Harness the power of artificial intelligence to transform your
                business and drive innovation
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-3 bg-white text-purple-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Get Started
                </Link>
                <Link
                  // to="/services"
                  to="#"
                  onClick={scrollIntoView}
                  className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-purple-600 transition-colors font-medium"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}
      {/* Benefits Section */}
      <section id="learn-more-ai" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose AI Powered Solutions?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leverage artificial intelligence to gain competitive advantages
              and drive business growth
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AI Powered Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI solutions designed to solve complex business
              challenges
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center text-gray-600"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AI Technologies We Use
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge AI and machine learning technologies for optimal
              results
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiTechnologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg mr-4">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {tech.name}
                  </h3>
                </div>
                <p className="text-gray-600">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AI Use Cases by Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how AI is transforming different industries and business
              processes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {useCase.icon}
                  <h3 className="text-xl font-semibold text-gray-900 ml-3">
                    {useCase.industry}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {useCase.applications.map((app, appIndex) => (
                    <div
                      key={appIndex}
                      className="text-sm text-gray-600 bg-gray-50 p-2 rounded"
                    >
                      {app}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from clients who have successfully implemented AI solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {testimonial.role} at {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Go AI Powered?
            </h2>
            <p className="text-xl mb-8 text-purple-100">
              Transform your business with intelligent AI solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-purple-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Start Your AI Journey
              </Link>
              <Link 
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-purple-600 transition-colors font-medium"
              >
                Explore AI Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}
      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs />
      </section>
    </div>
  );
};

export default AIPoweredPage;
